import { Avatar, Button, Link, Stack, Typography } from '@mui/material';
import { IApplication, getUploadedFile } from 'API/applies/applies';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useImposter, useIsDesktop } from 'Hooks';
import { DateDivider } from './DateDivider';
import { format } from 'date-fns';
import { APPLY_TYPES } from 'Enum/Applies';
import { LogoSmall, PdfDocument } from 'Images';
import { ChatBubble } from './ChatBubble';
import { IconAndText } from './ChatBubble/IconAndText';
import { isObjectNotEmpty } from 'Utils/isObjectEmpty';
import { shortDateFormat } from 'Config';
import { downloadFile, getShortText } from 'Utils';

interface IDetailsBody {
  application: IApplication;
}

export const DetailsBody = ({ application }: IDetailsBody) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.Applies);
  const { imposterId, authenticatedUser, fakeUser } = useImposter();
  const isDesktop = useIsDesktop();

  const getText = () => {
    if (application.userNote) return application.userNote;
    else
      return application.type === APPLY_TYPES.telephone
        ? `${dictionary.phoneRequestOpened} ${application.title}`
        : `${dictionary.webRequestOpened} ${application.title}`;
  };

  return (
    <Stack gap='32px' padding={'32px 0 40px 0'}>
      {(application.createDate || application.shemeshCreateDate) && (
        <>
          <DateDivider
            text={`${dictionary.applyOpenDate} ${format(
              new Date(application.createDate || application.shemeshCreateDate),
              shortDateFormat
            )}`}
          />
          <ChatBubble
            minWidth={isDesktop ? '300px' : 'auto'}
            maxWidth='458px'
            isRtl={true}
          >
            <IconAndText
              isUsersText={true}
              title={
                imposterId
                  ? `${fakeUser.firstName} ${fakeUser.lastName}`
                  : `${authenticatedUser.firstName} ${authenticatedUser.lastName}`
              }
              text={getText()}
            />
            {application?.files?.length > 0 && (
              <Stack
                flexDirection='row'
                flexWrap={'wrap'}
                width={'auto'}
                m='0 24px 16px 42px'
                gap='24px'
              >
                {application.files.map((file) => (
                  <Button
                    key={file.id}
                    sx={{
                      width: '200px',
                      height: '42px',
                      gap: '5px',
                      bgcolor: '#DBE7FA',
                      borderRadius: '8px',
                      p: '7px 12px 9px',
                    }}
                    onClick={async () => {
                      const fileData = await getUploadedFile(file.id);

                      if (fileData?.fileContentBase64) {
                        downloadFile(
                          fileData.fileContentBase64,
                          file.userGivenName.split('.').pop()!,
                          file.userGivenName
                        );
                      }
                    }}
                  >
                    <Avatar
                      sx={{ width: '14px', height: '22px' }}
                      src={PdfDocument}
                      alt={dictionary.documentToDownload}
                    />
                    <Typography
                      variant={'body2'}
                      title={file.userGivenName}
                      fontSize='18px'
                    >
                      {getShortText(file.userGivenName, 16)}
                    </Typography>
                  </Button>
                ))}
              </Stack>
            )}
          </ChatBubble>
        </>
      )}
      {application.shemeshCreateDate && (
        <>
          <DateDivider
            text={`${dictionary.updateDate} ${format(
              new Date(application.shemeshCreateDate),
              shortDateFormat
            )}`}
          />
          <ChatBubble minWidth={isDesktop ? '300px' : 'auto'} isRtl={false}>
            <IconAndText
              isUsersText={false}
              title={dictionary.rehabilitationDepartment}
              text={dictionary.applyOpened(application.title)}
            />
          </ChatBubble>
        </>
      )}
      {application.forwardedForProcessing && (
        <>
          <DateDivider
            text={`${dictionary.updateDate} ${format(
              new Date(application.forwardedForProcessing),
              shortDateFormat
            )}`}
          />
          <ChatBubble minWidth={isDesktop ? '300px' : 'auto'} isRtl={false}>
            <IconAndText
              isUsersText={false}
              title={dictionary.rehabilitationDepartment}
              text={dictionary.yourRequestWasTransferred}
            />
          </ChatBubble>
        </>
      )}
      {application.replyDate && (
        <>
          <DateDivider
            text={`${dictionary.updateDate} ${format(
              new Date(application.replyDate),
              shortDateFormat
            )}`}
          />
          <ChatBubble minWidth={isDesktop ? '300px' : 'auto'} isRtl={false}>
            <Stack gap='8px' direction='row' p='16px 16px 16px 8px'>
              <Avatar
                src={LogoSmall}
                alt=''
                sx={{ width: '28px', height: '28px' }}
              />
              <Stack pt='3px' gap='9px'>
                <Typography
                  fontSize={isDesktop ? '16px' : '14px'}
                  lineHeight={isDesktop ? '22px' : '20px'}
                  fontWeight='700'
                >
                  {dictionary.rehabilitationDepartment}
                </Typography>
                <Typography
                  sx={{ textDecorationLine: 'underline' }}
                  fontSize={isDesktop ? '18px' : '16px'}
                  lineHeight={isDesktop ? '24px' : '22px'}
                >
                  {dictionary.replyDetails}
                </Typography>
                <Typography
                  fontSize={isDesktop ? '18px' : '16px'}
                  lineHeight={isDesktop ? '24px' : '22px'}
                  variant='body3'
                >
                  {application.reply.trim() || dictionary.noReplyDetails}
                </Typography>
              </Stack>
            </Stack>
          </ChatBubble>
        </>
      )}
      {application.link && isObjectNotEmpty(application.link) && (
        <ChatBubble minWidth={isDesktop ? '300px' : 'auto'} isRtl={false}>
          <Stack gap='6px' maxWidth='320px' p='16px'>
            <Typography
              variant={'body4'}
              fontSize={isDesktop ? '18px' : undefined}
              tabIndex={0}
            >
              {application.link.linkPrefixText}
            </Typography>
            <Stack direction={'row'} gap={'4px'} flexWrap={'wrap'}>
              <Link
                fontSize={isDesktop ? '18px' : undefined}
                href={application.link.linkUrl}
              >
                <Typography variant={'body4'}>
                  {application.link.linkText}
                </Typography>
              </Link>
              <Typography variant={'body3'}>
                {application.link.linkSuffixText}
              </Typography>
            </Stack>
          </Stack>
        </ChatBubble>
      )}
    </Stack>
  );
};
